import React, { useCallback, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  ArrowLeft,
  AlertCircle,
  Loader2,
  Pencil,
  Monitor,
  RefreshCw,
  Share2,
} from 'lucide-react';
import DashboardBlockCard from '@/components/dashboard-builder/DashboardBlockCard';
import ControlBar from '@/components/dashboard-builder/controls/ControlBar';
import ShareSettingsModal from '@/components/dashboard-builder/ShareSettingsModal';
import { useDashboardViewer } from '@/hooks/useDashboardViewer';
import { useRelativeTime } from '@/hooks/useRelativeTime';
import type { RenderHandle } from '@/types/dashboard';

const DashboardViewPage: React.FC = () => {
  const { id: dashboardId } = useParams<{ id: string }>();
  const {
    dashboard,
    blocks,
    blockData,
    controls,
    activeControlValues,
    isLoading,
    isRefreshing,
    error,
    renderHandles,
    changeControlValue,
    refreshData,
    lastRefreshedAt,
    isPolling,
  } = useDashboardViewer(dashboardId);

  const [showShareModal, setShowShareModal] = useState(false);

  const isLive = dashboard?.data_mode === 'live';
  const isSaved = dashboard?.status === 'saved';
  const relativeTime = useRelativeTime(lastRefreshedAt);

  const handleRenderHandle = useCallback(
    (handle: RenderHandle) => {
      renderHandles.current.set(handle.blockId, handle);
    },
    [renderHandles]
  );

  const handleDisposeHandle = useCallback(
    (blockId: string) => {
      renderHandles.current.delete(blockId);
    },
    [renderHandles]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">
            Failed to load dashboard
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            {error}
          </p>
          <Link
            to="/dashboards"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboards
          </Link>
        </div>
      </div>
    );
  }

  const layoutColumns = dashboard?.layout_columns ?? 2;
  const hasBlocks = blocks.length > 0;

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Top bar */}
      <div className="flex-shrink-0 h-12 px-4 flex items-center gap-3 border-b border-border bg-background">
        <Link
          to="/dashboards"
          className="p-1.5 rounded-lg hover:bg-muted transition-colors"
        >
          <ArrowLeft className="w-4 h-4 text-muted-foreground" />
        </Link>

        <div className="flex items-center gap-2 min-w-0 flex-1">
          <h1 className="text-sm font-semibold text-foreground truncate">
            {dashboard?.name || 'Dashboard'}
          </h1>
          {isLive && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-success/10 text-success border border-success/20 flex-shrink-0">
              <span className={clsx(
                'w-1.5 h-1.5 rounded-full bg-success',
                isPolling && 'animate-pulse'
              )} />
              Live
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {isLive && relativeTime && (
            <span className="text-[11px] text-muted-foreground tabular-nums">
              Updated {relativeTime}
            </span>
          )}

          <button
            onClick={refreshData}
            disabled={isRefreshing}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
              'text-muted-foreground hover:bg-accent',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            title="Refresh data"
          >
            <RefreshCw
              className={clsx(
                'w-3.5 h-3.5',
                isRefreshing && 'animate-spin'
              )}
            />
            Refresh
          </button>

          {isSaved && (
            <button
              onClick={() => setShowShareModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-muted-foreground hover:bg-accent transition-colors"
            >
              <Share2 className="w-3.5 h-3.5" />
              Share
            </button>
          )}

          <Link
            to={`/dashboards/${dashboardId}/build`}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-muted-foreground hover:bg-accent transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit
          </Link>

          <Link
            to={`/dashboards/${dashboardId}/cast`}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-muted-foreground hover:bg-accent transition-colors"
          >
            <Monitor className="w-3.5 h-3.5" />
            Cast
          </Link>
        </div>
      </div>

      {/* Control bar */}
      <ControlBar
        controls={controls}
        activeControlValues={activeControlValues}
        isRefreshing={isRefreshing}
        onChangeControlValue={changeControlValue}
      />

      {/* Block grid */}
      <div className="flex-1 overflow-y-auto">
        {hasBlocks ? (
          <div
            className="p-4 gap-4"
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${layoutColumns}, 1fr)`,
              alignContent: 'start',
            }}
          >
            {blocks.map((block) => {
              const data = blockData.get(block.id) ?? [];
              const spanW = Math.min(block.position.w, layoutColumns);

              return (
                <div key={block.id} style={{ gridColumn: `span ${spanW}` }}>
                  <DashboardBlockCard
                    block={block}
                    data={data}
                    viewOnly
                    onRenderHandle={handleRenderHandle}
                    onDisposeHandle={handleDisposeHandle}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center min-h-[400px]">
            <div className="text-center max-w-sm px-4">
              <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                No charts to display
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                This dashboard has no accepted blocks yet. Go to Build Mode to
                add charts.
              </p>
              <Link
                to={`/dashboards/${dashboardId}/build`}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary"
              >
                <Pencil className="w-4 h-4" />
                Open Build Mode
              </Link>
            </div>
          </div>
        )}
      </div>

      {dashboardId && dashboard && (
        <ShareSettingsModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          dashboardId={dashboardId}
          dashboardName={dashboard.name}
        />
      )}
    </div>
  );
};

export default DashboardViewPage;
